import { useContext, useEffect, useState } from "react";

import SimpleModal from "../../../../shared/components/simple-modal/simple-modal";
import TextInput from "../../../../shared/components/text-input/text-input";
import AuthContext from "../../../../shared/context/auth-context";
import { IEnumerationOption } from "../../../../shared/definitions/datatypes";
import useHttp from "../../../../shared/hooks/use-http";
import { useHttpNonTable } from "../../../../shared/hooks/use-httpNonTable";
import { failedToast, getAllDataNonTable, postSingle } from "../../../../shared/utils/api";

import "./domain-type-popup.scss";
import { RxCross2 } from "react-icons/rx";

import { usePrompt } from "../../../../shared/utils/use-prompt";
import { createUUID } from "../../../../shared/utils/utils";

import { Button } from "react-bootstrap";
import { LiaPlusSolid } from "react-icons/lia";

import ConfirmationPopup from "../../../../shared/components/confirmation-popup/confirmation-popup";
import Checkbox from "../../../components/checkbox/checkbox";
import { ITableRowData } from "../../../components/data-table/data-table";
import SearchInput from "../../../components/search-input/search-input";

interface IDomainTypeSelectorProps {
  show: boolean;
  domainType: ITableRowData | undefined;
  onPopupClosed(result: ITableRowData | undefined): void;
  isDomainTypeUpdated(isUpdated: boolean): void;
}

const emptyDomainType = {
  id: "",
  name: "",
  active: true,
  alertcase_id_prefix: "",
  stages: [{ id: createUUID(), name: "", screen_id: "", position: 1 }]
};

const DomainTypePopup = ({
  show,
  domainType,
  onPopupClosed,
  isDomainTypeUpdated
}: IDomainTypeSelectorProps) => {
  const authCtx = useContext(AuthContext);

  const [domainTypeData, setDomainTypeData] = useState<ITableRowData>(domainType || emptyDomainType);
  const [screens, setScreens] = useState<ITableRowData[]>([]);
  const [isDirty, setIsDirty] = useState(false);
  const { flag, setFlag } = usePrompt(isDirty);

  const {
    sendRequest: getTypes,
    status: getTypesStatus,
    data: getTypesData,
    error: getTypesError
  } = useHttpNonTable(getAllDataNonTable, false, authCtx.token, undefined, "manager");
  
  const { 
    sendRequest: postDomainType, 
    status: postDomainTypeStatus, 
    error: postDomainTypeError, 
    data: postDomainTypeData 
  } = useHttp(postSingle, false, authCtx.token, "domainType",  "manager");
  
  useEffect(() => {
    getTypes("screens?order_by=name asc");
  }, []);

  useEffect(() => {
    if (getTypesData && getTypesStatus === "completed" && !getTypesError) {
      setScreens(getTypesData);
    }
  }, [getTypesData, getTypesStatus, getTypesError]);

  useEffect(() => {
    setIsDirty(false);
    setDomainTypeData(domainType || emptyDomainType);
  }, [domainType]);

  useEffect(() => {
    if (postDomainTypeData && postDomainTypeStatus === "completed" && !postDomainTypeError) {
      onPopupClosed(postDomainTypeData);
      setIsDirty(false);
      isDomainTypeUpdated(true);
    }
  }, [postDomainTypeData, postDomainTypeStatus, postDomainTypeError]);

  const handleOnDomainChange = (newValue: string, key?: string) => {
    if (!key) {
      return;
    }
    setIsDirty(true);
    setDomainTypeData({ ...domainTypeData, [key]: newValue });
  };

  const handleOnActiveChange = () => {
    setIsDirty(true);
    setDomainTypeData({ ...domainTypeData, active: !domainTypeData.active });
  };

  const handleOnStageChange = (newValue: string, key: string| undefined, stageIndex: number) => {
    if (!key) {
      return;
    }
    setIsDirty(true);
    const newStages = [...domainTypeData.stages];
    newStages[stageIndex][key] = newValue;
    setDomainTypeData({ ...domainTypeData, stages: newStages });
  };

  const handleSubmit = () => {
    if (
      domainTypeData.name === "" ||
      domainTypeData.alertcase_id_prefix === "" ||
      domainTypeData.stages.length === 0 ||
      domainTypeData.stages.some((s: ITableRowData) => s.name === "" || s.screen_id === "" || s.position === 0)
    ) {
      failedToast("Please fill in all required fields");
      return;
    }
    const domainTypePayload = {...domainTypeData, id: domainTypeData.id || createUUID()};
    postDomainType(`domain_type/${domainTypePayload.id}`, domainTypePayload);
  };

  const handlePopupClosed = (result: ITableRowData | undefined) => {
    setFlag(isDirty);
    if (!isDirty) {
      onPopupClosed(result);
    }
  };

  const handleDeleteStage = (index: number) => {
    setIsDirty(true);
    const newStages = [...domainTypeData.stages];
    newStages.splice(index, 1);
    setDomainTypeData({ ...domainTypeData, stages: newStages.map((s: ITableRowData, index: number) => ({
      ...s,
      position: index + 1
    })) });
  };

  const handleAddStage = () => {
    setIsDirty(true);
    if (domainTypeData.stages.length > 0 && domainTypeData.stages[domainTypeData.stages.length - 1].name === "") {
      return;
    }
    const newStages = [...domainTypeData.stages];
    newStages.push({ id: createUUID(), name: "", screen_id: "", position: domainTypeData.stages.length + 1 });
    setDomainTypeData({ ...domainTypeData, stages: newStages });
  };

  const repositionStages = (stage: ITableRowData) => {
    const filteredStages = domainTypeData.stages.filter((s: ITableRowData) => s.id !== stage?.id);

    const insertIndex = Math.max(0, stage?.position - 1);

    filteredStages.splice(insertIndex, 0, stage);

    setDomainTypeData({
      ...domainTypeData,
      stages: filteredStages.map((s: ITableRowData, index: number) => ({
        ...s,
        position: index + 1
      }))
    });
  };

  const renderModalBody = () => {
    return <div>
      <Checkbox 
        label="Domain type is active"
        checked={domainTypeData?.active}
        onClick={handleOnActiveChange}
      />
      <TextInput
        label="Name"
        type="text"
        id="domainType"
        required={true}
        inputKey="name"
        controlled={true}
        handleOnChange={handleOnDomainChange}
        value={domainTypeData?.name}
      />
     
      <TextInput
        label="AlertCase ID Prefix"
        type="text"
        id="domainType"
        required={true}
        inputKey="alertcase_id_prefix"
        controlled={true}
        handleOnChange={handleOnDomainChange}
        value={domainTypeData?.alertcase_id_prefix}
      />

      <div className="domainTypePopup-stages">
        <h4><b>Stages:</b></h4>
        <div className="domainTypePopup-stage">
          <div className="domainTypePopup-stagePosition"><b>Position *</b></div>
          <div className="domainTypePopup-stageName"><b>Name *</b></div>
          <div className="domainTypePopup-stageScreen"><b>Screen *</b></div>
        </div>
        {domainTypeData?.stages?.map((stage: ITableRowData, i: number) => (
          <div className="domainTypePopup-stage" key={i}>
            <div className="domainTypePopup-stagePosition">
              <TextInput
                type={"number"}
                onlyWholeNumbers={true}
                id={`stagePositionId${i}`}
                required={true}
                inputKey={"position"}
                errorMsg={"Please provide the stage position"}
                autofocus={false}
                controlled={true}
                value={stage.position}
                handleOnChange={(newValue: string, key?: string) => handleOnStageChange(newValue, key, i)}
                handleOnBlur={() => repositionStages(stage)}
                generateUniqueId={true}
              />
            </div>
            <div className="domainTypePopup-stageName">
              <TextInput
                type={"text"}
                id={`stageNameId ${i}`}
                required={true}
                inputKey={"name"}
                autofocus={false}
                controlled={true}
                errorMsg={"Please provide the stage name"}
                value={stage.name}
                placeholder={"Enter stage name"}
                handleOnChange={(newValue: string, key?: string) => handleOnStageChange(newValue, key, i)}
                generateUniqueId={true}
              />
            </div>
            <div className="domainTypePopup-stageScreen">
              <SearchInput
                label=''
                inputKey='screen_id'
                required={true}
                data={screens?.map((screen: ITableRowData) => ({ id: screen.id, name: screen.name }))}
                selectedOption={stage.screen_id || ""}
                handleOnChange={(option: IEnumerationOption | undefined) => handleOnStageChange(option?.id || "", "screen_id", i)}
              />
            </div>
            <div className="domainTypePopup-stageDelete" onClick={() => handleDeleteStage(i)}>
              <RxCross2 />
            </div>
          </div>
        ))}
        <Button
          name="add"
          onClick={() => handleAddStage()}
          className="domainTypePopup-addStage"
          variant="primary">
          <LiaPlusSolid />
        </Button>
      </div>
    </div>;
  };

  return <div className="domainTypePopup">
    <ConfirmationPopup
      show={flag}
      handleSaveClick={() => {
        handleSubmit();
      }}
      handlePopupClosed={() => {
        setFlag(false);
      }}
      handleContinue={() => {
        setIsDirty(false);
        setFlag(false);
        onPopupClosed(undefined);
      }} />
    <SimpleModal
      modalBody={renderModalBody()}
      mode={"Domain Type"}
      handleModalClosed={() => handlePopupClosed(undefined)}
      handleSubmit={handleSubmit}
      show={show}
      buttonText={domainTypeData?.id ? "Save" : "Add"}
    />
  </div>;
};

export default DomainTypePopup;
